import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { usePatchCatalogItemMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useMusic24Capabilities } from '../../../../music24/hooks/useMusic24Capabilities';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { dayOf, isoOfDay, ReleaseChoice } from '../addSongFlowSteps';
import { lastReleaseDay, releaseDayProblem } from '../releaseDayRange';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseStepProps {
  accountId: number;
  item: CatalogItem;
  eyebrow: string;
  today: string;
  choice: ReleaseChoice | undefined;
  onChoice: (choice: ReleaseChoice) => void;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}

/** The stored values (as the old form sent them) and their labels. */
const RELEASE_TYPES = [
  { value: 'Single', label: addSongFlowMessages.typeSingle },
  { value: 'EP', label: addSongFlowMessages.typeEp },
  { value: 'Album', label: addSongFlowMessages.typeAlbum },
] as const;

/**
 * Step 2 (design catalog-add-song b1, b2): when the song comes out. "On a date" opens the date and
 * the type; "No date yet" clears the date; "It is already out" turns step 3 into the link question.
 * `PATCH .../catalog/:uuid { releaseDate, releaseType }`.
 */
const ReleaseStepComponent: React.FC<ReleaseStepProps> = ({
  accountId,
  item,
  eyebrow,
  today,
  choice,
  onChoice,
  track,
  onBack,
  onNext,
  onSkip,
}) => {
  const intl = useIntl();
  const [day, setDay] = useState(dayOf(item.releaseDate));
  const [releaseType, setReleaseType] = useState(item.releaseType ?? '');
  const [hasFailed, setFailed] = useState(false);
  const [patchItem, patching] = usePatchCatalogItemMutation();
  // Only a plan with pre-saves is promised a pre-save page (QA F03): Finish makes it then.
  const { canUsePresave } = useMusic24Capabilities();
  // Next checks the day; the error shows after the first press (QA brk-10).
  const [isChecked, setChecked] = useState(false);
  const dateRef = useRef<HTMLInputElement>(null);
  const dayProblem = choice === 'date' ? releaseDayProblem(day, today) : null;

  // "On a date" opens the day field under the three cards: on a phone it was out of view, and
  // Next stayed off with no reason (QA mob-16). The field comes into view and takes the focus.
  useEffect(() => {
    if (choice !== 'date' || dateRef.current === null || dayOf(item.releaseDate) !== '') {
      return;
    }
    dateRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    dateRef.current.focus({ preventScroll: true });
  }, [choice, item.releaseDate]);

  const choices = useMemo(
    () =>
      [
        {
          value: 'date',
          title: addSongFlowMessages.releaseOnDate,
          body: canUsePresave ? addSongFlowMessages.releaseOnDateBody : addSongFlowMessages.releaseOnDateBodyNoPresave,
        },
        { value: 'noDate', title: addSongFlowMessages.releaseNoDate, body: addSongFlowMessages.releaseNoDateBody },
        { value: 'out', title: addSongFlowMessages.releaseOut, body: addSongFlowMessages.releaseOutBody },
      ] as const,
    [canUsePresave]
  );

  const canGoOn = choice !== undefined;

  const save = useCallback(() => {
    if (choice === undefined) {
      return;
    }
    if (dayProblem !== null) {
      setChecked(true);
      dateRef.current?.focus();
      return;
    }
    if (choice === 'out') {
      onNext();
      return;
    }
    const releaseDate = choice === 'date' ? isoOfDay(day) : null;
    const type = releaseType === '' ? null : releaseType;
    if (dayOf(item.releaseDate) === dayOf(releaseDate) && (item.releaseType ?? null) === type) {
      onNext();
      return;
    }
    setFailed(false);
    track(patchItem({ accountId, itemUuid: item.uuid, releaseDate, releaseType: type }).unwrap())
      .then(() => onNext())
      .catch(() => setFailed(true));
  }, [accountId, choice, day, dayProblem, item, onNext, patchItem, releaseType, track]);

  let dayError: string | undefined;
  if (isChecked && dayProblem === 'missing') {
    dayError = intl.formatMessage(addSongFlowMessages.releaseDateMissing);
  } else if (isChecked && dayProblem === 'past') {
    dayError = intl.formatMessage(addSongFlowMessages.releaseDatePast);
  } else if (isChecked && dayProblem === 'tooFar') {
    dayError = intl.formatMessage(addSongFlowMessages.releaseDateTooFar);
  }

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(addSongFlowMessages.releaseQuestion, { title: item.title })}
        sub={intl.formatMessage(
          canUsePresave ? addSongFlowMessages.releaseSub : addSongFlowMessages.releaseSubNoPresave
        )}
      />
      <div className={styles.choices} role="radiogroup">
        {choices.map((one) => (
          <button
            key={one.value}
            type="button"
            role="radio"
            aria-checked={choice === one.value}
            className={clsx(styles.choice, choice === one.value && styles.choiceOn)}
            onClick={() => onChoice(one.value)}
            data-testid={`add-song-release-${one.value}`}
          >
            <span className={styles.radio} />
            <b>{intl.formatMessage(one.title)}</b>
            <small>{intl.formatMessage(one.body)}</small>
          </button>
        ))}
      </div>
      {choice === 'date' && (
        <div className={styles.two}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="add-song-date">
              {intl.formatMessage(addSongFlowMessages.releaseDate)}
            </label>
            <input
              ref={dateRef}
              id="add-song-date"
              type="date"
              className={clsx(styles.input, dayError !== undefined && styles.inputBad)}
              value={day}
              min={today}
              max={lastReleaseDay(today)}
              aria-invalid={dayError !== undefined}
              aria-describedby={dayError !== undefined ? 'add-song-date-error' : undefined}
              onChange={(event) => setDay(event.target.value)}
            />
            {dayError !== undefined && (
              <span className={styles.error} role="alert" id="add-song-date-error">
                {dayError}
              </span>
            )}
            {isChecked && dayProblem === 'past' && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => onChoice('out')}
                data-testid="add-song-release-past-out"
              >
                {intl.formatMessage(addSongFlowMessages.releaseDatePastOut)}
              </button>
            )}
          </div>
          <div className={styles.field}>
            <span className={styles.label} id="add-song-type-label">
              {intl.formatMessage(addSongFlowMessages.releaseType)}{' '}
              <span className={styles.optional}>· {intl.formatMessage(addSongFlowMessages.optional)}</span>
            </span>
            <div className={styles.segment} role="group" aria-labelledby="add-song-type-label">
              {RELEASE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  aria-pressed={releaseType === type.value}
                  className={clsx(releaseType === type.value && styles.segmentOn)}
                  data-testid={`add-song-type-${type.value}`}
                  onClick={() => setReleaseType(releaseType === type.value ? '' : type.value)}
                >
                  {intl.formatMessage(type.label)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {choice === 'out' && (
        <div className={styles.alertInfo}>{intl.formatMessage(addSongFlowMessages.releaseOutInfo)}</div>
      )}
      {hasFailed && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(addSongFlowMessages.saveFailed)}
        </div>
      )}
      <StepActions
        onBack={onBack}
        onSkip={onSkip}
        // A Next that is off says why (QA imp-3, mob-16).
        offHint={canGoOn ? undefined : intl.formatMessage(addSongFlowMessages.releaseNextHint)}
        main={{
          label: intl.formatMessage(hasFailed ? addSongFlowMessages.tryAgain : addSongFlowMessages.next),
          onClick: save,
          disabled: !canGoOn,
          isLoading: patching.isLoading,
        }}
      />
    </>
  );
};

export const ReleaseStep = TypedMemo(ReleaseStepComponent);
